import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSessionQuery } from "../../../auth/hooks";
import { useClientBranding } from "../../../../shared/hooks";
import { useDepartments } from "../../../../shared/components/Filters/hooks";
import { clientBrandingApi, type UpdateClientBrandingRequest } from "../../../../shared/api";
import { qk } from "../../../../lib/queryKeys";
import {
  clientManagementApi,
  type CreateSingleUserRequest,
  type CreateSingleUserResponse,
  type ResetUserPasswordResponse,
  type ServiceOverrides,
} from "../api/clientManagement";
import {
  BrandingTab,
  BulkUploadTab,
  ClientTab,
  DashboardAccessTab,
  DepartmentsTab,
  JobRolesTab,
  QuestionsTab,
  RegionsTab,
  DepotsTab,
  UserManagementTab,
} from "../components";
import {
  buildPayload,
  makePreview,
  validateHex,
  validatePalette,
  toFormState,
} from "../utils/branding";
import {
  emptyFormState,
  emptyManagementState,
  type BrandingFormState,
  type ClientManagementState,
} from "../types";
import { ROUTES } from "../../../../shared/utils";

const CLIENT_DETAIL_TABS = [
  { value: "overview", label: "Overview" },
  { value: "branding", label: "Branding" },
  { value: "regions", label: "Regions" },
  { value: "depots", label: "Depots" },
  { value: "departments", label: "Departments" },
  { value: "job-roles", label: "Job Roles" },
  { value: "dashboard-access", label: "Dashboard Access", centralAdminOnly: true },
  { value: "questions", label: "Questions", centralAdminOnly: true },
  { value: "users", label: "Users" },
  { value: "bulk-upload", label: "Bulk Upload", centralAdminOnly: true },
];

export const ClientDetail: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSessionQuery();
  const isCentralAdmin = session?.user?.is_central_admin ?? false;
  const sessionClientId = session?.user?.client_id ?? null;

  // Non-central admins use their own client ID
  const effectiveClientId = clientId ?? sessionClientId;

  const [activeTab, setActiveTab] = useState("overview");
  const [formState, setFormState] = useState<BrandingFormState>(emptyFormState);
  const [managementState, setManagementState] = useState<ClientManagementState>(emptyManagementState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadedClientId, setLoadedClientId] = useState<string | null>(null);
  const [updatingUserServicesId, setUpdatingUserServicesId] = useState<string | null>(null);
  const previousClientIdRef = useRef<string | null>(null);

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: brandingData, isLoading: brandingLoading, error: brandingError } =
    useClientBranding(effectiveClientId);

  const clientDetailsQuery = useQuery({
    queryKey: qk.clientDetails(effectiveClientId),
    queryFn: () => clientManagementApi.getClientDetails(effectiveClientId as string),
    enabled: !!effectiveClientId,
    staleTime: 2 * 60 * 1000,
  });

  // Derived from client settings — central admins always have full access;
  // client admins can access User Management only when anonymisation is off
  // OR when the client explicitly enables user management access.
  const clientData = clientDetailsQuery.data?.data ?? null;
  const anonymisationEnabled = Boolean(
    clientData?.enable_anonymisation ?? clientData?.enableAnonymisation
  );
  const userManagementEnabled = Boolean(
    clientData?.enable_user_management ?? clientData?.enableUserManagement
  );
  const userManagementAllowed =
    isCentralAdmin ||
    !anonymisationEnabled ||
    userManagementEnabled;

  const tabs = CLIENT_DETAIL_TABS.filter((t) => {
    if (t.centralAdminOnly && !isCentralAdmin) return false;
    // Hide the Users tab for client admins while loading or when not permitted
    if (t.value === "users" && !isCentralAdmin && !userManagementAllowed) return false;
    return true;
  });

  const clientUserCountQuery = useQuery({
    queryKey: qk.clientUserCount(effectiveClientId),
    queryFn: () => clientManagementApi.getClientUserCount(effectiveClientId as string),
    enabled: !!effectiveClientId,
    staleTime: 2 * 60 * 1000,
  });

  const departmentsQuery = useDepartments();

  const selectedDepartmentsQuery = useQuery({
    queryKey: qk.centralDepartments(effectiveClientId),
    queryFn: () => clientManagementApi.listCentralDepartments(effectiveClientId),
    enabled: isCentralAdmin && !!effectiveClientId,
    staleTime: 2 * 60 * 1000,
  });

  const departmentsList = isCentralAdmin
    ? selectedDepartmentsQuery.data?.data ?? []
    : departmentsQuery.data?.data ?? [];
  const departmentsLoading = isCentralAdmin
    ? selectedDepartmentsQuery.isLoading
    : departmentsQuery.isLoading;

  const jobRolesQuery = useQuery({
    queryKey: qk.clientJobRoles(effectiveClientId),
    queryFn: () => clientManagementApi.listJobRoles(effectiveClientId as string),
    enabled: !!effectiveClientId,
    staleTime: 5 * 60 * 1000,
  });

  const regionsQuery = useQuery({
    queryKey: qk.clientRegions(effectiveClientId),
    queryFn: () => clientManagementApi.listRegions(effectiveClientId as string),
    enabled: !!effectiveClientId,
    staleTime: 5 * 60 * 1000,
  });

  const depotsQuery = useQuery({
    queryKey: qk.clientDepots(effectiveClientId),
    queryFn: () => clientManagementApi.listDepots(effectiveClientId as string),
    enabled: !!effectiveClientId,
    staleTime: 5 * 60 * 1000,
  });

  const clientUsersQuery = useQuery({
    queryKey: qk.clientUsers(effectiveClientId),
    queryFn: () => clientManagementApi.listClientUsers(effectiveClientId as string),
    enabled: !!effectiveClientId,
    staleTime: 2 * 60 * 1000,
  });

  const centralUsersAllQuery = useQuery({
    queryKey: qk.centralUsers("all"),
    queryFn: () => clientManagementApi.listCentralUsers(),
    enabled: isCentralAdmin,
    staleTime: 2 * 60 * 1000,
  });

  const dashboardSettingsQuery = useQuery({
    queryKey: qk.dashboardSettings(effectiveClientId),
    queryFn: () => clientManagementApi.listClientDashboardSettings(effectiveClientId as string),
    enabled: isCentralAdmin && !!effectiveClientId,
    staleTime: 2 * 60 * 1000,
  });

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (previousClientIdRef.current === effectiveClientId) return;
    previousClientIdRef.current = effectiveClientId;
    setFormState(emptyFormState);
    setErrors({});
    setLoadedClientId(null);
  }, [effectiveClientId]);

  useEffect(() => {
    if (!effectiveClientId) return;
    if (loadedClientId === effectiveClientId) return;
    if (!brandingData) return;
    setFormState(toFormState(brandingData.data));
    setLoadedClientId(effectiveClientId);
  }, [brandingData, effectiveClientId, loadedClientId]);

  useEffect(() => {
    if (!effectiveClientId) return;
    if (loadedClientId !== effectiveClientId) {
      queryClient.invalidateQueries({ queryKey: qk.clientBranding(effectiveClientId) });
    }
  }, [effectiveClientId, loadedClientId, queryClient]);

  // ── Mutations ──────────────────────────────────────────────────────────────

  const brandingMutation = useMutation({
    mutationFn: (payload: UpdateClientBrandingRequest) =>
      clientBrandingApi.updateClientBranding(effectiveClientId as string, payload),
    onSuccess: (response) => {
      queryClient.setQueryData(qk.clientBranding(effectiveClientId), response);
      setFormState(toFormState(response.data));
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: (payload: Partial<{
      name: string;
      is_active: boolean;
      enable_anonymisation: boolean;
      enable_user_management: boolean;
      learning_locker_enabled: boolean;
      occupational_health_enabled: boolean;
      wearables_enabled: boolean;
      akea_life_enabled: boolean;
      sso_enabled: boolean;
      sso_domains: string | null;
    }>) => clientManagementApi.updateClient(effectiveClientId as string, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientDetails(effectiveClientId) });
      queryClient.invalidateQueries({ queryKey: qk.clients() });
    },
  });

  const deleteClientMutation = useMutation({
    mutationFn: () => clientManagementApi.deleteClient(effectiveClientId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clients() });
      navigate(ROUTES.SETTINGS_CLIENTS);
    },
  });

  const createDepartmentMutation = useMutation({
    mutationFn: (payload: { name: string; is_active: boolean }) =>
      clientManagementApi.createDepartment(effectiveClientId as string, payload),
    onSuccess: () => {
      setManagementState((prev) => ({ ...prev, departmentName: "", departmentActive: true }));
      queryClient.invalidateQueries({ queryKey: ["filter-options", "departments"] });
      queryClient.invalidateQueries({ queryKey: qk.centralDepartments(effectiveClientId) });
    },
  });

  const updateDepartmentMutation = useMutation({
    mutationFn: ({ departmentId, payload }: { departmentId: string; payload: { name?: string; is_active?: boolean } }) =>
      clientManagementApi.updateDepartment(departmentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["filter-options", "departments"] });
      queryClient.invalidateQueries({ queryKey: qk.centralDepartments(effectiveClientId) });
    },
  });

  const deleteDepartmentMutation = useMutation({
    mutationFn: (departmentId: string) => clientManagementApi.deleteDepartment(departmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["filter-options", "departments"] });
      queryClient.invalidateQueries({ queryKey: qk.centralDepartments(effectiveClientId) });
    },
  });

  const createJobRoleMutation = useMutation({
    mutationFn: (payload: { name: string; is_active: boolean }) =>
      clientManagementApi.createJobRole(effectiveClientId as string, payload),
    onSuccess: () => {
      setManagementState((prev) => ({ ...prev, jobRoleName: "", jobRoleActive: true }));
      queryClient.invalidateQueries({ queryKey: qk.clientJobRoles(effectiveClientId) });
      queryClient.invalidateQueries({ queryKey: qk.centralJobRoles(effectiveClientId) });
    },
  });

  const updateJobRoleMutation = useMutation({
    mutationFn: ({ jobRoleId, payload }: { jobRoleId: string; payload: { name?: string; is_active?: boolean } }) =>
      clientManagementApi.updateJobRole(jobRoleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientJobRoles(effectiveClientId) });
    },
  });

  const deleteJobRoleMutation = useMutation({
    mutationFn: (jobRoleId: string) => clientManagementApi.deleteJobRole(jobRoleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientJobRoles(effectiveClientId) });
      queryClient.invalidateQueries({ queryKey: qk.centralJobRoles(effectiveClientId) });
    },
  });

  const createRegionMutation = useMutation({
    mutationFn: (payload: { name: string; is_active: boolean }) =>
      clientManagementApi.createRegion(effectiveClientId as string, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientRegions(effectiveClientId) });
    },
  });

  const updateRegionMutation = useMutation({
    mutationFn: ({ regionId, payload }: { regionId: string; payload: { name?: string; is_active?: boolean } }) =>
      clientManagementApi.updateRegion(regionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientRegions(effectiveClientId) });
    },
  });

  const deleteRegionMutation = useMutation({
    mutationFn: (regionId: string) => clientManagementApi.deleteRegion(regionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientRegions(effectiveClientId) });
    },
  });

  const assignUserToRegionMutation = useMutation({
    mutationFn: ({ regionId, userId }: { regionId: string; userId: string }) =>
      clientManagementApi.assignUserToRegion(regionId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
    },
  });

  const removeUserFromRegionMutation = useMutation({
    mutationFn: ({ regionId, userId }: { regionId: string; userId: string }) =>
      clientManagementApi.removeUserFromRegion(regionId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
    },
  });

  const createDepotMutation = useMutation({
    mutationFn: (payload: { name: string; is_active: boolean }) =>
      clientManagementApi.createDepot(effectiveClientId as string, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientDepots(effectiveClientId) });
    },
  });

  const updateDepotMutation = useMutation({
    mutationFn: ({ depotId, payload }: { depotId: string; payload: { name?: string; is_active?: boolean } }) =>
      clientManagementApi.updateDepot(depotId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientDepots(effectiveClientId) });
    },
  });

  const deleteDepotMutation = useMutation({
    mutationFn: (depotId: string) => clientManagementApi.deleteDepot(depotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientDepots(effectiveClientId) });
    },
  });

  const assignUserToDepotMutation = useMutation({
    mutationFn: ({ depotId, userId }: { depotId: string; userId: string }) =>
      clientManagementApi.assignUserToDepot(depotId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
    },
  });

  const removeUserFromDepotMutation = useMutation({
    mutationFn: ({ depotId, userId }: { depotId: string; userId: string }) =>
      clientManagementApi.removeUserFromDepot(depotId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
    },
  });

  const updateDashboardSettingMutation = useMutation({
    mutationFn: ({ dashboardKey, payload }: { dashboardKey: string; payload: { is_enabled?: boolean; ai_enabled?: boolean | null } }) =>
      clientManagementApi.updateClientDashboardSetting(effectiveClientId as string, dashboardKey, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.dashboardSettings(effectiveClientId) });
    },
  });

  const assignUserToClientMutation = useMutation({
    mutationFn: (userId: string) =>
      clientManagementApi.assignUserToClient(effectiveClientId as string, userId),
    onSuccess: () => {
      setManagementState((prev) => ({ ...prev, assignUserId: "" }));
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
    },
  });

  const removeUserFromClientMutation = useMutation({
    mutationFn: (userId: string) => clientManagementApi.removeUserFromClient(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => clientManagementApi.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
      queryClient.invalidateQueries({ queryKey: qk.centralUsers("all") });
    },
  });

  const assignUserToDepartmentMutation = useMutation({
    mutationFn: ({ departmentId, userId }: { departmentId: string; userId: string }) =>
      clientManagementApi.assignUserToDepartment(departmentId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
    },
  });

  const removeUserFromDepartmentMutation = useMutation({
    mutationFn: ({ departmentId, userId }: { departmentId: string; userId: string }) =>
      clientManagementApi.removeUserFromDepartment(departmentId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
    },
  });

  const assignUserToJobRoleMutation = useMutation({
    mutationFn: ({ jobRoleId, userId }: { jobRoleId: string; userId: string }) =>
      clientManagementApi.assignUserToJobRole(jobRoleId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
    },
  });

  const removeUserFromJobRoleMutation = useMutation({
    mutationFn: ({ jobRoleId, userId }: { jobRoleId: string; userId: string }) =>
      clientManagementApi.removeUserFromJobRole(jobRoleId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
    },
  });

  const updateUserAdminMutation = useMutation({
    mutationFn: ({ userId, isAdmin }: { userId: string; isAdmin: boolean }) =>
      clientManagementApi.updateUserAdmin(userId, isAdmin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
      queryClient.invalidateQueries({ queryKey: qk.centralUsers("all") });
    },
  });

  const updateUserServicesMutation = useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: Partial<ServiceOverrides> }) =>
      clientManagementApi.updateUserServices(userId, payload),
    onMutate: ({ userId }) => setUpdatingUserServicesId(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientUsers(effectiveClientId) });
      queryClient.invalidateQueries({ queryKey: qk.centralUsers("all") });
    },
    onSettled: () => setUpdatingUserServicesId(null),
  });

  const resetUserPasswordMutation = useMutation<
    ResetUserPasswordResponse,
    unknown,
    { userId: string; temporary: boolean; sendEmail: boolean; password?: string; loginUrl?: string }
  >({
    mutationFn: ({ userId, temporary, sendEmail, password, loginUrl }) =>
      clientManagementApi.resetUserPassword(userId, {
        temporary,
        send_email: sendEmail,
        ...(password ? { password } : {}),
        ...(loginUrl ? { login_url: loginUrl } : {}),
      }),
  });

  const createUserMutation = useMutation<
    CreateSingleUserResponse,
    unknown,
    { clientId: string; payload: CreateSingleUserRequest }
  >({
    mutationFn: ({ clientId: cId, payload }) =>
      clientManagementApi.createSingleUser(cId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.clientUsers(effectiveClientId),
      });
      queryClient.invalidateQueries({
        queryKey: qk.centralUsers("all"),
      });
    },
  });

  // ── Branding helpers ───────────────────────────────────────────────────────

  const handleBrandingChange = (field: keyof BrandingFormState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleManagementChange = (field: keyof ClientManagementState, value: string | boolean) => {
    setManagementState((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    const colorFields: (keyof BrandingFormState)[] = [
      "primary_color", "secondary_color", "accent_color", "background_color", "text_color",
      "dark_primary_color", "dark_secondary_color", "dark_accent_color", "dark_background_color", "dark_text_color",
      "chart_primary_color", "chart_secondary_color", "chart_accent_color",
      "dark_chart_primary_color", "dark_chart_secondary_color", "dark_chart_accent_color",
    ];
    colorFields.forEach((field) => {
      if (!validateHex(formState[field])) nextErrors[field] = "Enter a valid hex colour.";
    });
    const cp = validatePalette(formState.chart_palette);
    if (!cp.valid) nextErrors.chart_palette = cp.message || "Invalid.";
    const dcp = validatePalette(formState.dark_chart_palette);
    if (!dcp.valid) nextErrors.dark_chart_palette = dcp.message || "Invalid.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleBrandingSubmit = () => {
    if (!effectiveClientId || !validate()) return;
    brandingMutation.mutate(buildPayload(formState));
  };

  const lightPreview = useMemo(() => makePreview(formState, "light"), [formState]);
  const darkPreview = useMemo(() => makePreview(formState, "dark"), [formState]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const clientName = clientDetailsQuery.data?.data?.name ?? null;

  return (
    <Box sx={{ p: { xs: 2, md: 2.5 }, display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Back */}
      {isCentralAdmin && (
        <Box mb={1.5}>
          <Button
            variant="text"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTES.SETTINGS_CLIENTS)}
            sx={{ px: 0, color: "text.secondary", minWidth: 0 }}
          >
            All clients
          </Button>
        </Box>
      )}

      {/* Heading */}
      <Box mb={2}>
        {clientDetailsQuery.isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Stack>
        ) : (
          <Typography variant="h6" fontWeight={700} lineHeight={1.3}>
            {clientName ?? "Client Settings"}
          </Typography>
        )}
      </Box>

      {/* Tab bar */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 3,
          flexShrink: 0,
          minHeight: 40,
          "& .MuiTab-root": {
            minHeight: 40,
            py: 0,
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
            sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }}
          />
        ))}
      </Tabs>

      {/* Tab content */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {!effectiveClientId ? (
          <Alert severity="info">No client selected.</Alert>
        ) : (
          <>
            {activeTab === "overview" && (
              <ClientTab
                effectiveClientId={effectiveClientId}
                isLoading={clientDetailsQuery.isLoading}
                error={clientDetailsQuery.error}
                client={clientDetailsQuery.data?.data ?? null}
                isCentralAdmin={isCentralAdmin}
                onToggleAnonymisation={(value) =>
                  updateClientMutation.mutate({ enable_anonymisation: value })
                }
                onToggleUserManagement={(value) =>
                  updateClientMutation.mutate({ enable_user_management: value })
                }
                onToggleClientService={(field, value) =>
                  updateClientMutation.mutate({ [field]: value })
                }
                anonymisationPending={updateClientMutation.isPending}
                createClientForm={{ name: "", is_active: true, learning_locker_enabled: false, occupational_health_enabled: false, wearables_enabled: false, akea_life_enabled: false }}
                onCreateClientChange={() => {}}
                onCreateClient={() => {}}
                createClientPending={false}
                createClientResult={null}
                onDismissCreateClientResult={() => {}}
                onUpdateClientName={(name) => updateClientMutation.mutate({ name })}
                updateClientNamePending={updateClientMutation.isPending}
                onDeleteClient={() => deleteClientMutation.mutate()}
                deleteClientPending={deleteClientMutation.isPending}
                deleteClientError={deleteClientMutation.isError}
                userCount={clientUserCountQuery.data?.data?.count ?? 0}
                onUpdateSso={(payload) => updateClientMutation.mutate(payload)}
                ssoUpdatePending={updateClientMutation.isPending}
              />
            )}

            {activeTab === "branding" && (
              <BrandingTab
                effectiveClientId={effectiveClientId}
                isLoading={brandingLoading}
                error={brandingError}
                formState={formState}
                errors={errors}
                onChange={handleBrandingChange}
                onSubmit={handleBrandingSubmit}
                isSaving={brandingMutation.isPending}
                isSaveError={brandingMutation.isError}
                isSaveSuccess={brandingMutation.isSuccess}
                lightPreview={lightPreview}
                darkPreview={darkPreview}
              />
            )}

            {activeTab === "job-roles" && (
              <JobRolesTab
                effectiveClientId={effectiveClientId}
                managementState={managementState}
                onChange={handleManagementChange}
                createJobRoleMutation={createJobRoleMutation}
                updateJobRoleMutation={updateJobRoleMutation}
                deleteJobRoleMutation={deleteJobRoleMutation}
                jobRolesQuery={jobRolesQuery}
              />
            )}

            {activeTab === "departments" && (
              <DepartmentsTab
                effectiveClientId={effectiveClientId}
                managementState={managementState}
                onChange={handleManagementChange}
                createDepartmentMutation={createDepartmentMutation}
                updateDepartmentMutation={updateDepartmentMutation}
                deleteDepartmentMutation={deleteDepartmentMutation}
                departmentsLoading={departmentsLoading}
                departmentsList={departmentsList}
              />
            )}

            {activeTab === "regions" && (
              <RegionsTab
                effectiveClientId={effectiveClientId}
                createRegionMutation={createRegionMutation}
                updateRegionMutation={updateRegionMutation}
                deleteRegionMutation={deleteRegionMutation}
                regionsLoading={regionsQuery.isLoading}
                regionsList={regionsQuery.data?.data ?? []}
              />
            )}

            {activeTab === "depots" && (
              <DepotsTab
                effectiveClientId={effectiveClientId}
                createDepotMutation={createDepotMutation}
                updateDepotMutation={updateDepotMutation}
                deleteDepotMutation={deleteDepotMutation}
                depotsLoading={depotsQuery.isLoading}
                depotsList={depotsQuery.data?.data ?? []}
              />
            )}

            {activeTab === "dashboard-access" && isCentralAdmin && (
              <DashboardAccessTab
                effectiveClientId={effectiveClientId}
                dashboardSettingsQuery={dashboardSettingsQuery}
                updateDashboardSettingMutation={updateDashboardSettingMutation}
              />
            )}

            {activeTab === "questions" && isCentralAdmin && (
              <QuestionsTab
                effectiveClientId={effectiveClientId}
                isCentralAdmin={isCentralAdmin}
              />
            )}

            {activeTab === "users" && userManagementAllowed && (
              <UserManagementTab
                effectiveClientId={effectiveClientId}
                isCentralAdmin={isCentralAdmin}
                managementState={managementState}
                setAssignUserId={(value) =>
                  setManagementState((prev) => ({ ...prev, assignUserId: value }))
                }
                assignUserToClientMutation={assignUserToClientMutation}
                removeUserFromClientMutation={removeUserFromClientMutation}
                deleteUserMutation={deleteUserMutation}
                clientUsersQuery={clientUsersQuery}
                departmentsList={departmentsList}
                jobRolesQuery={jobRolesQuery}
                regionsList={regionsQuery.data?.data ?? []}
                depotsList={depotsQuery.data?.data ?? []}
                centralUsersAllQuery={centralUsersAllQuery}
                updateUserServicesMutation={updateUserServicesMutation}
                resetUserPasswordMutation={resetUserPasswordMutation}
                updatingUserServicesId={updatingUserServicesId}
                assignUserToDepartmentMutation={assignUserToDepartmentMutation}
                removeUserFromDepartmentMutation={removeUserFromDepartmentMutation}
                assignUserToJobRoleMutation={assignUserToJobRoleMutation}
                removeUserFromJobRoleMutation={removeUserFromJobRoleMutation}
                assignUserToRegionMutation={assignUserToRegionMutation}
                removeUserFromRegionMutation={removeUserFromRegionMutation}
                assignUserToDepotMutation={assignUserToDepotMutation}
                removeUserFromDepotMutation={removeUserFromDepotMutation}
                updateUserAdminMutation={updateUserAdminMutation}
                createUserMutation={createUserMutation}
              />
            )}

            {activeTab === "bulk-upload" && isCentralAdmin && (
              <BulkUploadTab
                effectiveClientId={effectiveClientId}
                isCentralAdmin={isCentralAdmin}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
